import lodash from 'lodash';
import {
    meInstitutionFeaturesSelector,
    meMonitorInstitutionFeaturesSelector,
    taskDocumentInstitutionIdSelector,
    binderPropertySelector,
    monitorInstitutionIdsSelector
} from './selectors';
import {
    binderInstitutionIdSelector,
    userDefaultInstitutionIdSelector,
} from 'common/selectors';
/**
 * Checks feature access for the institution a document is associated with.
 * @param {Object} state Redux state
 * @param {String} institutionId
 * @param {String} featureKey
 * @returns {Boolean}
 */
const documentFeatureAccess = (state, institutionId, featureKey) => {
    institutionId = institutionId || taskDocumentInstitutionIdSelector(state);

    const institutionFeatures = meInstitutionFeaturesSelector(state, institutionId);
    return institutionFeatures[featureKey] || false;
};

/**
 * Checks feature access for an institution or a binder's institution
 * @param {Object} state Redux state
 * @param {String} institutionId
 * @param {String} featureKey
 * @returns {Boolean}
 */
const institutionFeatureAccess = (state, institutionId, featureKey) => {
    institutionId = institutionId || binderInstitutionIdSelector(state);
    const institutionFeatures = meInstitutionFeaturesSelector(state, institutionId);
    return institutionFeatures[featureKey] || false;
};

/**
 * Checks feature access for a monitor at the current institution
 * @param {Object} state Redux state
 * @param {String} institutionId ID of current institution
 * @param {String} featureKey

 */
const monitorFeatureAccess = (state, institutionId, featureKey) => {
    institutionId = institutionId || binderInstitutionIdSelector(state);
    const monitorInstitutionFeatures = meMonitorInstitutionFeaturesSelector(state, institutionId);
    return monitorInstitutionFeatures[featureKey] || false;
};

/**
 * Checks feature access for a monitor for any of the monitor's institutions
 * @param {Object} state Redux state
 * @param {String} featureKey

 */
const monitorMultiInstitutionFeatureAccess = (state, featureKey) => {
    return lodash.some(
        monitorInstitutionIdsSelector(state),
        institutionId => {
            const monitorInstitutionFeatures = meMonitorInstitutionFeaturesSelector(state, institutionId);
            return monitorInstitutionFeatures[featureKey] || false;
        }
    );
};

/**
 * Checks feature access for a users default institution.
 * @param {Object} state Redux state
 * @param {String} institutionId
 * @param {String} featureKey
 * @returns {Boolean}
 */
const defaultInstitutionFeatureAccess = (state, institutionId, featureKey) => {
    institutionId = institutionId || userDefaultInstitutionIdSelector(state);

    const institutionFeatures = meInstitutionFeaturesSelector(state, institutionId);
    return institutionFeatures[featureKey] || false;
};

export const emailReminderFeatureAccess = (state, institutionId) => documentFeatureAccess(state, institutionId, 'emailReminders');
export const binderValidationFeatureAccess = (state, institutionId) => institutionFeatureAccess(state, institutionId, 'binderValidation');
export const doaFeatureAccess = (state, institutionId) => institutionFeatureAccess(state, institutionId, 'doa') || monitorFeatureAccess(state, institutionId, 'doa');

export const binderEventFeatureAccess = (state, institutionId) => {
    const hasInstitutionAccess = institutionFeatureAccess(state, institutionId, 'studyEvents');
    const hasMonitorAccess = monitorFeatureAccess(state, institutionId, 'studyEvents');
    const hasEventsTabEnabled = binderPropertySelector(state, 'eventsTabEnabled') || false;
    return hasEventsTabEnabled && (hasInstitutionAccess || hasMonitorAccess);
};

/**
 * Checks for study feature access
 * @param {Object} state Redux state
 * @param {String} institutionId
 */
export const studyFeatureAccess = (state, institutionId) => {
    const featureKey = 'study';
    institutionId = institutionId ||
        lodash.get(state, 'study.institutionId') ||
        userDefaultInstitutionIdSelector(state);
    const defaultFeatureAccess = defaultInstitutionFeatureAccess(state, institutionId, featureKey);
    let hasMonitorAccess;
    if (institutionId) {
        hasMonitorAccess = monitorFeatureAccess(state, institutionId, featureKey);
    } else {
        hasMonitorAccess = monitorMultiInstitutionFeatureAccess(state, featureKey);
    }
    return (defaultFeatureAccess || hasMonitorAccess);
};

export const dashboardFeatureAccess = (state, institutionId) => defaultInstitutionFeatureAccess(state, institutionId, 'dashboard');

export const studyEventFeatureAccess = (state, institutionId) => {
    const featureKey = 'studyEvents';
    institutionId = institutionId || lodash.get(state, 'study.institutionId');
    return defaultInstitutionFeatureAccess(state, institutionId, featureKey) || monitorFeatureAccess(state, institutionId, featureKey);
};

export const newDoaStudyFlowAccess = (state, institutionId) => {
    const featureKey = 'newDoaStudyFlow';
    institutionId = institutionId || lodash.get(state, 'study.institutionId');
    return defaultInstitutionFeatureAccess(state, institutionId, featureKey) || monitorFeatureAccess(state, institutionId, featureKey);
};
export const manageEventTypeFeatureAccess = (state, institutionId) => defaultInstitutionFeatureAccess(state, institutionId, 'manageEventTypes');

export const currentStudyEventAccess = (state) => {
    return studyEventFeatureAccess(state) && lodash.get(state, 'study.eventsTabEnabled', false);
};

export const currentStudyDelegationOfAuthorityAccess = (state) =>{
    return studyEventFeatureAccess(state) && lodash.get(state, 'study.delegationOfAuthorityEnabled', false);
} ;

export const loadHomePageV4Access = (state, institutionId) => {
    const featureKey = 'complion4.0';
    institutionId = institutionId || lodash.get(state, 'study.institutionId');
    return defaultInstitutionFeatureAccess(state, institutionId, featureKey);
};

